import Configs from "../../../src/vendorConfigs/Configs";
import { CustomEase } from "../CustomEase";
import { Ease } from "../Ease";
import { EaseFunction } from "../EaseFunction";
import { Timeline } from "../Timeline";
import GameEvents from "../const/GameEvents";
import SoundVars from "../const/SoundVars";
import IReelsConfig from "../managers/contracts/configurations/IReelsConfig";
import GameModel from "../model/GameModel";
import SpinModel from "../model/SpinModel";
import { OverlayType } from "../server/data/ISpinData";
import ConsoleUtil from "../utils/ConsoleUtil";
import SoundController from "../utils/SoundController";
import Reel from "../views/Reel";
import ReelSymbol from "../views/Symbol";
import AbstractReelBehaviour from "./AbstractReelBehaviour";

class ReelCascadeBehaviour extends AbstractReelBehaviour {
    public cascadeTL: Timeline | null;
    public symbolsDefaultPosition: number[];
    public symbolsFromNextGen: any[];
    public symPool: ReelSymbol[];
    public inCascade: boolean = false;
    protected _config: IReelsConfig = null;
    protected _stopDropEasing: EaseFunction =
        CustomEase.parse("back.inOut(0.6)");
    protected _spinDropEasing: EaseFunction = CustomEase.parse("back.out(0.6)");
    protected _stopDropEasingInTurboMode: EaseFunction = CustomEase.parse(
        Ease.None
    );
    protected _spinDropEasingInTurboMode: EaseFunction = CustomEase.parse(
        Ease.None
    );
    protected _fillRemainingEasing: EaseFunction = CustomEase.parse(Ease.None);
    protected _fillRemainingEasingInTurboMode: EaseFunction = CustomEase.parse(
        Ease.None
    );
    protected isStopPlaying: boolean = false;
    /**
     * Value must be in ms (1000 ms = 1s)
     */
    public _animationStartDelay: number = 0;
    protected ts: number = 1;

    constructor(reel: Reel, sortFunction: () => void) {
        super(reel, sortFunction);
        this.parseConfig();
        this.symbolsDefaultPosition = [];
        this.symPool = [];
        this.getSymbolsDefaultPosition();
    }
    get config(): IReelsConfig {
        return this._config;
    }

    get spinSymbolAnimTime(): number {
        let result =
            this.config.cascadeProps.mainSpinProps.spinSymbolAnimTime / 1000;

        if (GameModel.turboSpin) {
            result =
                this.config.cascadeProps.mainSpinProps
                    .spinSymbolAnimTimeInTurboMode / 1000;
        }

        return result;
    }

    get spinFirstSymbolAnimTime(): number {
        let result =
            this.config.cascadeProps.mainSpinProps.spinFirstSymbolAnimTime /
            1000;

        if (GameModel.turboSpin) {
            result =
                this.config.cascadeProps.mainSpinProps.spinSymbolAnimTime /
                1000;
        }

        return result;
    }
    get stopSymbolAnimTime(): number {
        let result =
            this.config.cascadeProps.mainSpinProps.stopSymbolAnimTime / 1000;

        if (GameModel.turboSpin) {
            result =
                this.config.cascadeProps.mainSpinProps
                    .stopSymbolAnimTimeInTurboMode / 1000;
        }

        return result;
    }
    get fillRemainingAnimTime(): number {
        let result =
            this.config.cascadeProps.fillRemainingProps.animTime / 1000;

        if (GameModel.turboSpin) {
            result =
                this.config.cascadeProps.fillRemainingProps.animTimeInTurbo /
                1000;
        }

        return result;
    }

    get stopFirstSymbolAnimTime(): number {
        let result =
            this.config.cascadeProps.mainSpinProps.stopFirstSymbolAnimTime /
            1000;

        if (GameModel.turboSpin) {
            result =
                this.config.cascadeProps.mainSpinProps
                    .stopSymbolAnimTimeInTurboMode / 1000;
        }

        return result;
    }
    /**
     * @property value must be in ms (1000 ms = 1s)
     * @returns animation start delay in seconds (1s = 1000ms)
     */
    get animationStartDelay(): number {
        return this._animationStartDelay / 1000;
    }
    set animationStartDelay(value: number) {
        this._animationStartDelay = value;
    }
    get mainEasing(): EaseFunction {
        let result: EaseFunction;
        if (this.isStopPlaying) {
            if (GameModel.turboSpin) {
                result = this._stopDropEasingInTurboMode;
            } else {
                result = this._stopDropEasing;
            }
        } else {
            if (GameModel.turboSpin) {
                result = this._spinDropEasingInTurboMode;
            } else {
                result = this._spinDropEasing;
            }
        }

        return result;
    }
    get fillRemainingEasing(): EaseFunction {
        let result: EaseFunction;

        if (GameModel.turboSpin) {
            result = this._fillRemainingEasingInTurboMode;
        } else {
            result = this._fillRemainingEasing;
        }

        return result;
    }

    protected parseConfig(): void {
        this._config = Configs.reels;
        this.parseSpinEasing();
    }

    protected parseSpinEasing(): void {
        const config = Configs.reels;
        ///////////// SPIN TWEEN //////////////
        if (config?.cascadeProps?.mainSpinProps?.mainDropSpinEasing) {
            this._spinDropEasing = CustomEase.parse(
                config.cascadeProps.mainSpinProps.mainDropSpinEasing
            );
        }

        if (
            config?.cascadeProps?.mainSpinProps?.mainDropSpinEasingInTurboMode
        ) {
            this._spinDropEasingInTurboMode = CustomEase.parse(
                config.cascadeProps.mainSpinProps.mainDropSpinEasingInTurboMode
            );
        }
        ///////////// SPIN TWEEN //////////////

        ///////////// STOP TWEEN //////////////
        if (config?.cascadeProps?.mainSpinProps?.mainDropStopEasing) {
            this._stopDropEasing = CustomEase.parse(
                config.cascadeProps.mainSpinProps.mainDropStopEasing
            );
        }

        if (
            config?.cascadeProps?.mainSpinProps?.mainDropStopEasingInTurboMode
        ) {
            this._stopDropEasingInTurboMode = CustomEase.parse(
                config.cascadeProps.mainSpinProps.mainDropStopEasingInTurboMode
            );
        }
        ///////////// STOP TWEEN //////////////
        ///////////// FILL REMAINING TWEEN //////////////
        if (config?.cascadeProps?.fillRemainingProps?.easing) {
            this._fillRemainingEasing = CustomEase.parse(
                config.cascadeProps.fillRemainingProps.easing
            );
        }

        if (config?.cascadeProps?.fillRemainingProps?.easingInTurboMode) {
            this._fillRemainingEasingInTurboMode = CustomEase.parse(
                config.cascadeProps.fillRemainingProps.easingInTurboMode
            );
        }
        ///////////// FILL REMAINING TWEEN //////////////
    }

    // Override to change direction
    public get startSymbolsTweenPosition(): number {
        // Defined where the drop animations should start from
        // When regular cascade - it's above the reels
        // When reverse - it's below
        return this.reel.yOrigin - this.reel.symbols[0].height;
    }

    // Override to change direction
    public get endSymbolsTweenPosition(): number {
        // Same as the start - but where the symbols drop to
        // The opposite of the start position
        return this.reel.reelHeight + this.reel.symbols[0].height;
    }

    // Override to change direction
    protected getNextFromPool(): ReelSymbol {
        // Retrieve the symbol to be animated from the pool
        // In the regular cascade it should be last item, as it needs to fall first
        return this.symPool.pop() as ReelSymbol;
    }

    // Override to change direction
    protected getFillPosition(): number {
        // Retrieve the position where the next pool item should fall
        return this.symbolsDefaultPosition[this.symPool.length - 1];
    }

    // Override to change direction
    protected calculateSymAnimDuration(
        sym: ReelSymbol,
        targetPos: number,
        origDuration: number
    ): number {
        let distance = Math.abs(targetPos - sym.y);
        let origDistance = Math.abs(
            this.endSymbolsTweenPosition - this.reel.yOrigin
        );
        let time = (origDuration / origDistance) * distance;

        return time;
    }

    // Override to change direction
    protected get startSymbolIndex(): number {
        // From which symbol the cascade checks should start
        // As in regular cascade the bottom symbols drop first, we start from the bottom of the reel
        return this.reel.numSyms - 1;
    }

    // Override to change direction
    protected getNextSymbolIndex(currentIndex: number): number {
        // Calculates how the symbol animation progresses
        // In a regular cascade - it's bottom to top
        return currentIndex - 1;
    }

    // Override to change direction
    protected calculatePreviousExplodes(
        symIndex: number,
        explodedItems: boolean[]
    ): number {
        // Calculates the previously exploded symbols so we can drop on their spot/s
        let explodesBefore = 0;
        for (let index = this.reel.numSyms - 1; index > symIndex; index--) {
            let exploded = explodedItems[index];
            if (exploded) {
                explodesBefore++;
            }
        }

        return explodesBefore;
    }

    // Override to change direction
    protected calculatePreviousLocks(
        symIndex: number,
        lockedItems: number[],
        explodedItems: boolean[]
    ): number {
        //TODO - properly test when possible
        if (!lockedItems) {
            return 0;
        }
        let explodesBefore = 0;
        for (let index = 0; index < symIndex; index++) {
            let isLocked = this.isLocked(index, lockedItems);
            if (isLocked) {
                if (this.calculatePreviousExplodes(index, explodedItems) > 0) {
                    explodesBefore++;
                }
            }
        }

        return explodesBefore;
    }

    // Override to change direction
    protected calculateSymbolDrop(
        sym: ReelSymbol,
        symIndex: number,
        explodesBefore: number,
        lockedBefore: number,
        locked: boolean
    ): number {
        let targetY = sym.y;

        if (locked) {
            return targetY;
        } else if (explodesBefore) {
            targetY =
                this.symbolsDefaultPosition[
                    // TODO: check this math - explodes is plus, but is locked minus?
                    symIndex + explodesBefore - lockedBefore
                ];
        }
        return targetY;
    }

    // Override to change direction
    protected sendSymToPool(sym: ReelSymbol): number {
        // Set the position and visibility of the pool item
        this.positionInPool(sym);
        this.symPool.unshift(sym);
        return sym.y;
    }

    // Override to change direction
    protected positionInPool(sym: ReelSymbol) {
        sym.y = this.reel.yOrigin - sym.height;
        sym.visible = false;
    }

    public getSymbolsDefaultPosition() {
        const numSyms = this.reel.symbols.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            let sym = this.reel.symbols[symIdx];
            this.symbolsDefaultPosition.push(sym.y);
        }
    }

    // Use this to change ease for separate stages ( instead of overwriting functions)
    public changeEase(
        stopTime: number,
        easingFunction: EaseFunction = CustomEase.parse(Ease.Power2InOut)
    ) {
        this.stopTime = stopTime || 0.8;
        this.maxStopTime = stopTime * 2;

        this.easingFunction = easingFunction;
    }

    protected killTimeline() {
        if (this.cascadeTL) {
            this.cascadeTL.kill();
            this.cascadeTL.clear();
            this.cascadeTL = null;
        }
    }

    public spin(
        easingFunction: EaseFunction = CustomEase.parse(Ease.Power2InOut)
    ) {
        this.easingFunction = easingFunction;
        this.reel.sortSymbolsByY();
        this.inCascade = false;
        this.killTimeline();

        // Start the timeline for the cascade
        this.cascadeTL = new Timeline({
            timeScale: 1,
            paused: true,
            onComplete: this.onCompleteAnim,
            onUpdate: () => this.spinUpdate(),
            callbackScope: this,
        });

        this.isStopPlaying = false;

        if (this.isSpinning === false) {
            this.isSpinning = true;
            this.cascadeTL.timeScale = 1;
            this.spinMainDrop(false);
        }
    }
    /**
     *
     * @param explodesMap Matrix exploded symbols
     * @returns Start symbol delay for current reel
     */
    protected getExplodedSymDelay(explodesMap: any[]): number {
        let result = 0;
        let symbolDelay = 0;

        if (GameModel.turboSpin) {
            if (this.config?.cascadeProps?.dropRemainingProps?.delayInTurbo) {
                symbolDelay +=
                    this.config?.cascadeProps.dropRemainingProps.delayInTurbo /
                    1000;
            }
        } else {
            if (this.config?.cascadeProps?.dropRemainingProps?.delay) {
                symbolDelay +=
                    this.config?.cascadeProps.dropRemainingProps.delay / 1000;
            }
        }

        let prevExplodes = explodesMap.reduce((acc, r, ri) => {
            if (ri < this.reel.reelIndex) {
                (r as []).forEach((v) => (v ? (acc += 1) : null));
            }

            return acc;
        }, 0);

        result = prevExplodes * symbolDelay;

        return result;
    }

    public stop(stopSymbolData: any[], hasAnticipation: boolean) {
        if (!this.isStopPlaying) {
            this.killTimeline();
            this.cascadeTL = new Timeline({
                paused: true,
                timeScale: 1,
                onStart: () => {
                    this.onStopStartAnim(0.6);

                },
                onComplete: () => {
                    this.onStopCompleteAnim();
                },
                onUpdate: () => this.spinUpdate(),
                callbackScope: this,
            });
            window["cascadeTl" + this.reel.reelIndex] = this.cascadeTL;
            this.isStopPlaying = true;
            this.stopSymbols = stopSymbolData;
            // this.setFinalSymbols();
            if (!this.inCascade) {
                this.stopMainDrop(hasAnticipation);
            }
            this.isSpinning = false;
        }
    }

    public spinUpdate() {
        // if (GameModel.turboSpin === true && this.ts == 1) {
        //     this.ts = 3;
        //     this.activateQuickStop();
        // }
    }

    public activateQuickStop() {
        if (this.cascadeTL) {
            this.cascadeTL.timeScale = 3;
        }
    }

    protected onCompleteFillExplode(): void {
        SoundController.playSound(SoundVars.EFFECT_REEL_STOP);
    }
    protected onCompleteFillRemaining(hasAnticipation: boolean): void {
        SoundController.playSound(SoundVars.EFFECT_REEL_STOP);
        this.symCascadeComplete(hasAnticipation);
    }

    public fillExplodes(data: {explodesMap: any[], explodedItems: boolean[], lockedOverlay: number[]}) {
        //////////////////////////////////////////////////
        // this.easingFunction = easingFunction;
        this.reel.sortSymbolsByY();
        this.inCascade = false;
        this.killTimeline();

        // Start the timeline for the cascade
        this.cascadeTL = new Timeline({
            timeScale: 1,
            paused: true,
            onComplete: () => this.onCompleteFillExplode(),
            // onComplete: () => this.reel.sortSymbolsByY(),
            onUpdate: () => this.spinUpdate(),
            callbackScope: this,
        });

        this.isStopPlaying = false;

        if (this.isSpinning === false) {
            this.isSpinning = true;
            this.cascadeTL.timeScale = 1;

            /////////////////////////////////////////// Here place all logic
            // this.reel.sortSymbolsByY();
            this.inCascade = true;
            let easing = this.easingFunction;
            let index = this.startSymbolIndex;
            let duration = 0;
            let totalDuration = 0;
            let targetDelay = this.getExplodedSymDelay(data.explodesMap);

            if (GameModel.turboSpin) {
                if (this.config?.cascadeProps?.dropRemainingProps?.easingInTurbo) {
                    easing = CustomEase.parse(
                        this.config.cascadeProps.dropRemainingProps.easingInTurbo
                    );
                }
                // Convert in seconds !
                duration =
                    this.config.cascadeProps.dropRemainingProps
                        .animationDurationInTurbo / 1000;
            } else {
                if (this.config?.cascadeProps?.dropRemainingProps?.easing) {
                    easing = CustomEase.parse(
                        this.config.cascadeProps.dropRemainingProps.easing
                    );
                }
                // Convert in seconds !
                duration =
                    this.config.cascadeProps.dropRemainingProps
                        .animationDuration / 1000;
            }

            // Move symbols to the exploded gaps
            // Loop from the startIndex to the other end of the reel
            // Depends on the getNextSymbolIndex function to count indexes correctly
            while (index >= 0 && index < this.reel.symbols.length) {
                let exploded = data.explodedItems[index];
                let sym = this.reel.symbols[index];

                // If the symbol has just exploded - put it in the pool, where it will be hidden
                // Otherwise - calculate its new position base on previous explodes and locks
                let targetY = exploded
                    ? this.sendSymToPool(sym)
                    : this.calculateSymbolDrop(
                          sym,
                          index,
                          this.calculatePreviousExplodes(index, data.explodedItems),
                          this.calculatePreviousLocks(
                              index,
                              data.lockedOverlay,
                              data.explodedItems
                          ),
                          this.isLocked(index, data.lockedOverlay)
                      );

                if (targetY !== sym.y) {
                    totalDuration += duration;
                    this.cascadeTL?.to(
                        sym,
                        duration,
                        {
                            y: targetY,
                            ease: easing,
                            callbackScope: this,
                        },
                        targetDelay
                    );
                }
                index = this.getNextSymbolIndex(index);
            }

            if (totalDuration) {
                this.cascadeTL?.restart();
            } else {
                this.onCompleteAnim();
            }
        }
    }

    public fillRemaining(stopSymbolData: any[], hasAnticipation: boolean) {
        if (!this.isStopPlaying) {
            this.reel.sortSymbolsByY();
            this.killTimeline();
            this.cascadeTL = new Timeline({
                paused: true,
                timeScale: 1,
                onStart: () => {
                    this.onStopStartAnim(1);
                },
                onComplete: () => {
                    this.onStopCompleteAnim();

                },
                onUpdate: () => this.spinUpdate(),
                callbackScope: this,
            });
            window["cascadeTl" + this.reel.reelIndex] = this.cascadeTL;
            this.isStopPlaying = true;
            this.stopSymbols = stopSymbolData;

            let exploded = this.symPool.length;
            this.inCascade = false;
            this.cascadeTL?.pause();
            let targetDelay = this.getTargetDelayInFillRemaining();
            // If the exploded pool had items - we need to fill them up
            if (exploded) {
                // Take item from the pool and animate it to its new position
                while (this.symPool.length) {

                    let targetY = this.getFillPosition();
                    let newSymId = this.stopSymbols[this.symPool.length - 1];
                    let sym = this.getNextFromPool();
                    sym.setId(newSymId)
                    sym.y = this.startSymbolsTweenPosition;
                    sym.visible = true;

                    this.cascadeTL?.to(
                        sym,
                        this.fillRemainingAnimTime,
                        {
                            delay: this.animationStartDelay,
                            onComplete: () => this.onCompleteFillRemaining(hasAnticipation),
                            y: targetY,
                            ease: this.fillRemainingEasing,
                            callbackScope: this,
                        },
                        hasAnticipation ? "+=" + targetDelay : "<" + targetDelay
                    );
                }
                this.cascadeTL?.restart();
            } else {
                this.onStopCompleteAnim();
            }

            this.isSpinning = false;
        }
    }
    protected getTargetDelayInMainSpin(): number {
        // Delay in first halve of the main spin
        let targetDelay =
            Configs.reels.cascadeProps.mainSpinProps.spinSymbolDelay;
        if (GameModel.turboSpin) {
            if (this.isStopPlaying === true) {
                targetDelay =
                    Configs.reels.cascadeProps.mainSpinProps
                        .stopSymbolDelayInTurboMode;
            } else {
                targetDelay =
                    Configs.reels.cascadeProps.mainSpinProps
                        .spinSymbolDelayInTurboMode;
            }
        } else {
            // Delay in second halve of the main spin (spin stop)
            if (this.isStopPlaying === true) {
                targetDelay =
                    Configs.reels.cascadeProps.mainSpinProps.stopSymbolDelay;
            }
        }

        // Convert to seconds
        return targetDelay / 1000;
    }
    protected getTargetDelayInFillRemaining(): number {
        // Delay in first halve of the main spin
        let targetDelay =
            Configs.reels.cascadeProps.fillRemainingProps.symbolDelay;
        if (GameModel.turboSpin) {
            targetDelay =
                Configs.reels.cascadeProps.fillRemainingProps
                    .symbolDelayInTurboMode;
        }
        // Convert to seconds
        return targetDelay / 1000;
    }

    protected stopMainDrop(hasAnticipation: boolean) {
        this.cascadeTL?.clear();
        const startSym = this.startSymbolIndex;
        let symIdx = this.startSymbolIndex;

        // Loop the symbols from the start symbol till the other end of the reel
        // Depends on the getNextSymbolIndex function to count in the correct direction
        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            let targetDelay = this.getTargetDelayInMainSpin();
            let sym = this.reel.symbols[symIdx];
            let targetY = 0;
            let symId = this.stopSymbols[symIdx];
            sym.setId(symId);
            sym.y = this.startSymbolsTweenPosition;
            targetY = this.symbolsDefaultPosition[symIdx];

            if (symIdx != startSym) {
                this.cascadeTL?.to(
                    sym,
                    this.stopSymbolAnimTime,
                    {
                        y: targetY,
                        ease: this.mainEasing,
                        onComplete: () => {
                            this.symCascadeComplete(hasAnticipation);
                        },
                        callbackScope: this,
                    },
                    "<" + targetDelay
                );
            } else {
                this.cascadeTL?.to(
                    sym,
                    this.stopFirstSymbolAnimTime,
                    {
                        delay: this.animationStartDelay,
                        y: targetY,
                        ease: this.mainEasing,
                        onComplete: () => {
                            this.symCascadeComplete(hasAnticipation);
                        },
                        callbackScope: this,
                    },
                    hasAnticipation ? "+=" + targetDelay : "<" + targetDelay
                );
            }
            symIdx = this.getNextSymbolIndex(symIdx);
        }
        if (hasAnticipation) {
            this.cascadeTL?.add(() => {
                SoundController.playSound("anticipation");
            });
        }
        this.cascadeTL?.restart();
    }
    protected spinMainDrop(hasAnticipation: boolean) {
        this.cascadeTL?.clear();
        const startSym = this.startSymbolIndex;
        let symIdx = this.startSymbolIndex;

        // Loop the symbols from the start symbol till the other end of the reel
        // Depends on the getNextSymbolIndex function to count in the correct direction
        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            let targetDelay = this.getTargetDelayInMainSpin();
            let sym = this.reel.symbols[symIdx];
            let targetY = 0;
            targetY = this.endSymbolsTweenPosition;

            if (symIdx != startSym) {
                this.cascadeTL?.to(
                    sym,
                    this.spinSymbolAnimTime,
                    {
                        y: targetY,
                        ease: this.mainEasing,
                        onComplete: () => {
                            this.symCascadeComplete(hasAnticipation);
                        },
                        callbackScope: this,
                    },
                    "<" + targetDelay
                );
            } else {
                this.cascadeTL?.to(
                    sym,
                    this.spinFirstSymbolAnimTime,
                    {
                        delay: this.animationStartDelay,
                        y: targetY,
                        ease: this.mainEasing,
                        onComplete: () => {
                            this.symCascadeComplete(hasAnticipation);
                        },
                        callbackScope: this,
                    },
                    hasAnticipation ? "+=" + targetDelay : "<" + targetDelay
                );
            }
            symIdx = this.getNextSymbolIndex(symIdx);
        }
        if (hasAnticipation) {
            this.cascadeTL?.add(() => {
                SoundController.playSound("anticipation");
            });
        }
        this.cascadeTL?.restart();
    }

    public onCompleteAnim() {
        this.reel.emit(GameEvents.CASCADE_STOPPED, this.reel.reelIndex);
    }

    public onStopCompleteAnim() {
        if (this.isStopPlaying === true) {
            this.isSpinning = false;
            if (this.cascadeTL) {
                this.cascadeTL.timeScale = 1;
            }

            this.setFinalSymbols();
            this.ts = 1;
            //GameModel._turboSpin = false;
            SoundController.playSound(SoundVars.EFFECT_REEL_STOP);
            this.reel.emit(GameEvents.REEL_STOPPED, this.reel);
            this.reel.symbols.forEach((s, index) => {
                this.playHitAction(s.id, this.reel.reelIndex, index);
            });
        }
    }

    protected symCascadeComplete(hasAnticipation) {
        if (hasAnticipation) {
            SoundController.playSound(SoundVars.EFFECT_CASCADE_FALL_SECONDARY);
        }
    }

    protected onStopStartAnim(timescale = 1) {

    }

    protected setFinalSymbols() {
        this.reel.sortSymbolsByY();
        this.stopSymbols.forEach((sym, index) => {
            this.reel.symbols[index].setId(sym);
        });
    }

    protected isLocked(index: number, lockedItems: number[]): boolean {
        if (!lockedItems || lockedItems.length == 0) {
            return false;
        }
        return GameModel.gameConfig.symbolIds.indexOf(lockedItems[index]) != -1;
    }
}

export default ReelCascadeBehaviour;
